'use client'

import { CommBanner } from '@fukku/comms-banners/CommBanner'
import text from '@fukku/styles/texts.module.scss'
import { featureFlagKeys } from 'feature-flags/constants/featureFlags'
import { useFeatureFlag } from 'feature-flags/hooks/useFeatureFlag/useFeatureFlag'
import type { PromoBanner as PromoBannerType } from 'promo-banner/types/promoBanner'
import { isPromoActive } from 'promo-banner/utils/isPromoActive/isPromoActive'
import { useMemo } from 'react'
import { cx } from 'utils/cx'

export const DEFAULT_MAX_DAYS = 2

interface PromoBannerProps {
	readonly className?: string
	readonly promoBanner: PromoBannerType
}

export function PromoBanner({ className, promoBanner }: PromoBannerProps) {
	const { enabled: promoBannerEnabled } = useFeatureFlag(
		featureFlagKeys.PromoBanner
	)

	const {
		promotion: {
			uid,
			startDate,
			endDate,
			subtitle,
			info,
			title: promoTitle,
			links,
			discount,
		},
		type,
		backgroundColorType,
		customBackgroundColor,
		isInverse,
		showCountdown,
		timeBeforeEnd,
	} = promoBanner

	/**
	 * 	isPromoActive depends on the current date, useMemo avoids
	 *  unnecessary re-evaluations of the function on every render.
	 */
	const isActive = useMemo(
		() =>
			isPromoActive({
				startDate,
				endDate,
			}),
		[startDate, endDate]
	)

	if (!(promoBannerEnabled && isActive)) {
		return null
	}

	return (
		<CommBanner
			id={uid}
			type={type}
			title={promoTitle}
			discount={discount}
			links={links}
			subtitle={subtitle}
			infoText={info}
			bgColorType={backgroundColorType}
			inverse={isInverse}
			className={cx(text.bodyXL, className)}
			maxDays={timeBeforeEnd || DEFAULT_MAX_DAYS}
			bgColorCode={customBackgroundColor}
			{...(showCountdown && { countdown: endDate })}
		/>
	)
}
